import React, { lazy, Suspense, useEffect } from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';

import './index.css';
import './global.css';
import App from './App.jsx';

import { AuthProvider } from './contexts/user-context.jsx';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { SettingsProvider } from './contexts/settings-context.jsx';

import { queryClient, persister } from './config/query-client.js';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import './utils/i18next.js';
import { requestPersistentStorage } from './offline/utils/offline-helpers.js';

// Request persistent storage early to prevent browser from clearing
// IndexedDB (sync queue mutations, cached data) under storage pressure.
requestPersistentStorage().catch(console.error);

const DISABLE_QUERY_PERSIST = import.meta.env.VITE_DISABLE_QUERY_PERSIST === 'true';
const DISABLE_SW = import.meta.env.VITE_DISABLE_SW === 'true';

// Isolation toggles (set in .env for debugging):
// VITE_DISABLE_QUERY_PERSIST=true — QueryClientProvider only (no RQ IndexedDB restore)
// VITE_DISABLE_SW=true — unregister SW, skip registration in prod
// VITE_DISABLE_OFFLINE_API_CACHE=true — base.api GET bypasses offline-api IndexedDB layer
// VITE_DISABLE_HYDRATE=true — skip offline/index hydrateCriticalData
// VITE_CACHE_DEBUG=true — verbose cache / RQ persist / network logs (+ window.__GYM_CACHE_DEBUG__)


// Service Worker registration with better error handling
function unregisterServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.getRegistrations()
    .then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister().then((success) => {
          if (success) {
            console.log('[PWA] Unregistered legacy service worker:', registration.scope);
          }
        });
      });
    })
    .catch((error) => {
      console.error('[PWA] Service Worker cleanup failed:', error);
    });
}

function PWARegister() {
  useEffect(() => {
    if (DISABLE_SW) {
      unregisterServiceWorkers();
      console.log('[PWA] Service worker disabled via VITE_DISABLE_SW');
      return;
    }

    if (!('serviceWorker' in navigator)) return;

    if (import.meta.env.PROD) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('[PWA] Service Worker registered:', registration.scope);

          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                console.log('[PWA] New content available, please refresh');
              }
            });
          });
        })
        .catch((error) => {
          console.error('[PWA] Service Worker registration failed:', error);
        });
    } else {
      unregisterServiceWorkers();
    }
  }, []);

  return null;
}


// Lazy load devtools
const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({
    default: mod.ReactQueryDevtools,
  }))
);

// Devtools wrapper
const QueryDevtools = () => (
  <Suspense fallback={null}>
    <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
  </Suspense>
);


// Inner tree (shared between persist and non-persist providers)
function AppShell() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <PWARegister />
          <App />
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// App root
const rootTree = DISABLE_QUERY_PERSIST ? (
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AppShell />
        <QueryDevtools />
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>
) : (
  <StrictMode>
    <HelmetProvider>
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
        <AppShell />
        <QueryDevtools />
      </PersistQueryClientProvider>
    </HelmetProvider>
  </StrictMode>
);

createRoot(document.getElementById('root')).render(rootTree);